// src/components/Guide.jsx
// Landing page for shared guide links (startshepherd.com/guide/{id}).
//
// A visitor lands here two ways: they already have the Shepherd extension
// (which badges its toolbar icon and can run the guide directly — see
// background.js's deep-link handler in the extension repo), or they don't,
// in which case this page is the only thing they see and needs to get them
// installed. Detection uses a DOM marker (`data-shepherd-extension`) that
// the extension's landing-marker.js content script stamps onto this exact
// domain at document_start — there's no "externally_connectable" entry in
// the extension's manifest, so a runtime message ping isn't an option.
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const EXTENSION_DETECT_TIMEOUT_MS = 400; // marker is set at document_start; this is just a safety margin
const CWS_URL = 'https://chromewebstore.google.com/detail/shepherd-build-or-follow/okjbbpkkanbhfpmmafpmijcekpjjcncl';

function extensionDetected() {
  return document.documentElement.getAttribute('data-shepherd-extension') === 'installed';
}

export default function Guide() {
  const { id } = useParams();
  const [guide, setGuide] = useState(undefined); // undefined = loading, null = not found
  const [hasExtension, setHasExtension] = useState(null); // null = still checking

  useEffect(() => {
    let cancelled = false;
    getDoc(doc(db, 'playbooks', id))
      .then((snap) => {
        if (cancelled) return;
        setGuide(snap.exists() ? snap.data() : null);
      })
      .catch(() => { if (!cancelled) setGuide(null); });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    const t = setTimeout(() => setHasExtension(extensionDetected()), EXTENSION_DETECT_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, []);

  return (
    <section style={styles.section}>
      <div style={styles.card}>
        <div style={styles.brand}>Shepherd</div>

        {guide === undefined && <p style={styles.status}>Loading guide…</p>}
        {guide === null && <p style={styles.status}>This guide link looks broken or has been removed.</p>}
        {guide && (
          <>
            <h1 style={styles.goal}>{guide.goal}</h1>
            <p style={styles.meta}>{(guide.steps || []).length} steps{guide.universalUrl ? ` · ${guide.universalUrl}` : ''}</p>
          </>
        )}

        {hasExtension === null && <p style={styles.status}>Checking for the Shepherd extension…</p>}
        {hasExtension === true && (
          <p style={styles.status}>
            ✅ Opening in your Shepherd extension — click the <strong>Shepherd icon</strong> in your browser toolbar to continue.
          </p>
        )}
        {hasExtension === false && (
          <>
            <p style={styles.status}>Install Shepherd to follow this guide step by step, right on the page.</p>
            <a href={CWS_URL} target="_blank" rel="noreferrer" style={styles.btnPrimary}>
              Add Shepherd — it's free
            </a>
            <p style={styles.recheck}>
              Installed it?{' '}
              <a href="" onClick={(e) => { e.preventDefault(); window.location.reload(); }}>
                Click here to continue
              </a>
              .
            </p>
          </>
        )}

        <Link to="/" style={styles.backLink}>Back to home</Link>
      </div>
    </section>
  );
}

const styles = {
  section: {
    minHeight: '80vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'clamp(2rem, 6vw, 6rem) 1.5rem',
    marginTop: '64px',
    background: 'radial-gradient(ellipse 60% 70% at 70% 30%, rgba(26,115,232,0.07) 0%, transparent 70%), var(--warm-white)',
  },
  card: {
    maxWidth: 480,
    width: '100%',
    textAlign: 'center',
    background: 'white',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    padding: 'clamp(2rem, 5vw, 3rem)',
    boxShadow: '0 32px 80px rgba(26,31,54,0.08), 0 4px 16px rgba(26,31,54,0.04)',
    animation: 'fadeUp 0.6s ease both',
  },
  brand: {
    fontFamily: "'Sora', sans-serif",
    fontSize: '1.4rem',
    fontWeight: 700,
    color: 'var(--brand)',
    marginBottom: '1.25rem',
  },
  goal: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 'clamp(1.4rem, 3vw, 1.8rem)',
    fontWeight: 700,
    color: 'var(--charcoal)',
    marginBottom: '0.4rem',
  },
  meta: {
    fontSize: '0.9rem',
    color: 'var(--text-muted)',
    marginBottom: '1.5rem',
  },
  status: {
    fontSize: '1.05rem',
    color: 'var(--text-muted)',
    fontWeight: 300,
    lineHeight: 1.7,
    marginBottom: '1.5rem',
  },
  btnPrimary: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: 'var(--brand)', color: 'white',
    padding: '13px 26px', borderRadius: 'var(--radius-md)',
    fontSize: '1rem', fontWeight: 500,
    textDecoration: 'none', cursor: 'pointer',
  },
  recheck: {
    fontSize: '0.85rem',
    color: 'var(--text-muted)',
    marginTop: '1rem',
  },
  backLink: {
    display: 'block',
    marginTop: '2rem',
    fontSize: '0.85rem',
    color: 'var(--text-muted)',
  },
};
